import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Card, { CardHeader } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Alert from '@/components/ui/Alert';
import Spinner from '@/components/ui/Spinner';
import { securityApi } from '@/services/security';
import { ApiClientError } from '@/services/api';
import { parseAsUTC } from '@/utils/format';
import type { SecurityAlert, SecurityAlertType } from '@/types';

/**
 * Configuration for alert type display
 */
const alertTypeConfig: Record<SecurityAlertType, { icon: string; label: string; iconColor: string }> = {
  new_device: { icon: 'new', label: 'New Device Login', iconColor: 'text-blue-600' },
  impossible_travel: { icon: 'travel', label: 'Unusual Location', iconColor: 'text-orange-600' },
  failed_mfa: { icon: 'mfa', label: 'Failed MFA Attempts', iconColor: 'text-red-600' },
  sms_mfa_new_device: { icon: 'sms', label: 'SMS on New Device', iconColor: 'text-purple-600' },
};

/**
 * Format relative timestamp for display
 * Note: Timestamps from the backend are in UTC, so we parse them as UTC.
 */
function formatRelativeTime(dateString: string): string {
  const date = parseAsUTC(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins === 1 ? '' : 's'} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;

  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  });
}

/**
 * Get icon for alert type
 */
function AlertIcon({ type }: { type: SecurityAlertType }) {
  const config = alertTypeConfig[type] || alertTypeConfig.new_device;

  const icons: Record<string, JSX.Element> = {
    new: (
      <svg className={`h-5 w-5 ${config.iconColor}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    travel: (
      <svg className={`h-5 w-5 ${config.iconColor}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    mfa: (
      <svg className={`h-5 w-5 ${config.iconColor}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
      </svg>
    ),
    sms: (
      <svg className={`h-5 w-5 ${config.iconColor}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
      </svg>
    ),
  };

  return icons[config.icon] || icons.new;
}

/**
 * Get description for alert
 */
function getAlertDescription(alert: SecurityAlert): string {
  const { alertType, details } = alert;

  switch (alertType) {
    case 'new_device':
      return details.deviceName
        ? `New device login from ${details.deviceName}`
        : 'New device login detected';
    case 'impossible_travel':
      if (details.location && details.previousLocation) {
        return `Login from ${details.location} shortly after ${details.previousLocation}`;
      }
      return 'Login from unusual location detected';
    case 'failed_mfa':
      return details.failedAttempts
        ? `${details.failedAttempts} failed MFA attempt${details.failedAttempts === 1 ? '' : 's'}`
        : 'Multiple failed MFA attempts';
    case 'sms_mfa_new_device':
      return details.deviceName
        ? `SMS verification sent to ${details.deviceName}`
        : 'SMS verification sent to new device';
    default:
      return 'Security event detected';
  }
}

/**
 * Get severity badge color
 */
function getSeverityColor(severity: string): string {
  switch (severity) {
    case 'high':
      return 'bg-red-100 text-red-800';
    case 'medium':
      return 'bg-yellow-100 text-yellow-800';
    case 'low':
    default:
      return 'bg-gray-100 text-gray-800';
  }
}

export interface SecurityAlertListProps {
  className?: string;
  /** Maximum number of alerts to display */
  limit?: number;
  /** Show as compact banner instead of full list */
  compact?: boolean;
  /** Callback when alerts are acknowledged */
  onAlertsChange?: () => void;
}

/**
 * Security alerts list component
 * Shows recent security events that may require user attention
 */
export default function SecurityAlertList({
  className,
  limit = 10,
  compact = false,
  onAlertsChange,
}: SecurityAlertListProps) {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState<SecurityAlert[]>([]);
  const [unacknowledgedCount, setUnacknowledgedCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionInProgress, setActionInProgress] = useState<string | null>(null);
  // Track if feature is available
  const [isAvailable, setIsAvailable] = useState(true);

  // Fetch alerts
  const fetchAlerts = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await securityApi.getAlerts(limit);
      setAlerts(response.alerts);
      setUnacknowledgedCount(response.unacknowledgedCount);
    } catch (err) {
      if (err instanceof ApiClientError) {
        // Hide component if endpoint doesn't exist (404)
        if (err.status === 404) {
          setIsAvailable(false);
          return;
        }
        setError(err.detail);
      } else {
        setError('Failed to load security alerts.');
      }
    } finally {
      setIsLoading(false);
    }
  }, [limit]);

  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  // Handle acknowledge single alert
  const handleAcknowledge = async (alertId: string) => {
    setActionInProgress(alertId);
    setError(null);

    try {
      await securityApi.acknowledgeAlert(alertId);
      await fetchAlerts();
      onAlertsChange?.();
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.detail);
      } else {
        setError('Failed to acknowledge alert.');
      }
    } finally {
      setActionInProgress(null);
    }
  };

  // Handle acknowledge all alerts
  const handleAcknowledgeAll = async () => {
    setActionInProgress('all');
    setError(null);

    try {
      await securityApi.acknowledgeAllAlerts();
      await fetchAlerts();
      onAlertsChange?.();
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.detail);
      } else {
        setError('Failed to acknowledge alerts.');
      }
    } finally {
      setActionInProgress(null);
    }
  };

  // Hide component if feature is not available (404)
  if (!isAvailable) {
    return null;
  }

  // Compact banner view
  if (compact) {
    if (isLoading || unacknowledgedCount === 0) {
      return null;
    }

    return (
      <Alert variant="warning" className={className}>
        <div className="flex items-center justify-between">
          <span>
            {unacknowledgedCount} security alert{unacknowledgedCount === 1 ? '' : 's'} require{unacknowledgedCount === 1 ? 's' : ''} your attention
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/settings/security')}
          >
            View
          </Button>
        </div>
      </Alert>
    );
  }

  // Full list view
  if (isLoading) {
    return (
      <Card className={className}>
        <CardHeader
          title="Security Alerts"
          description="Recent security events for your account"
        />
        <div className="flex justify-center py-8">
          <Spinner size="lg" />
        </div>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader
        title="Security Alerts"
        description="Recent security events for your account"
        action={
          unacknowledgedCount > 0 ? (
            <Button
              variant="outline"
              size="sm"
              onClick={handleAcknowledgeAll}
              disabled={actionInProgress === 'all'}
              isLoading={actionInProgress === 'all'}
            >
              Dismiss all
            </Button>
          ) : undefined
        }
      />

      {error && (
        <Alert variant="error" className="mb-4" onDismiss={() => setError(null)}>
          {error}
        </Alert>
      )}

      {alerts.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <svg
            className="mx-auto h-12 w-12 text-gray-400 mb-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <p>No security alerts</p>
          <p className="text-sm mt-1">Your account looks secure!</p>
        </div>
      ) : (
        <div className="space-y-3">
          {alerts.map((alert) => {
            const config = alertTypeConfig[alert.alertType] || alertTypeConfig.new_device;
            const isAcknowledged = alert.acknowledgedAt !== null;

            return (
              <div
                key={alert.id}
                className={`flex items-start justify-between p-4 rounded-lg ${
                  isAcknowledged ? 'bg-gray-50' : 'bg-yellow-50 border border-yellow-200'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 mt-0.5">
                    <AlertIcon type={alert.alertType} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className={`font-medium ${isAcknowledged ? 'text-gray-700' : 'text-gray-900'}`}>
                        {config.label}
                      </span>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getSeverityColor(alert.severity)}`}>
                        {alert.severity}
                      </span>
                    </div>
                    <p className={`text-sm mt-1 ${isAcknowledged ? 'text-gray-500' : 'text-gray-700'}`}>
                      {getAlertDescription(alert)}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      {formatRelativeTime(alert.createdAt)}
                      {alert.details.location && ` - ${alert.details.location}`}
                    </p>
                  </div>
                </div>
                {!isAcknowledged && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleAcknowledge(alert.id)}
                    disabled={actionInProgress === alert.id}
                    isLoading={actionInProgress === alert.id}
                    className="text-gray-600"
                  >
                    Dismiss
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}
